import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import logger from '../lib/logger';

/**
 * Dedicated OAuth callback handler.
 *
 * Per Emergent Auth playbook, the session_id in the URL fragment must be
 * consumed ONCE, synchronously during render (before route matching), to
 * avoid race conditions. iOS Safari in particular surfaces these races
 * because its event-loop ordering differs from desktop browsers.
 *
 * Previously this component silently bounced the user back to /login on
 * any failure — which caused a frustrating redirect loop in production
 * where nobody could see the real error. It now renders a diagnostic
 * screen capturing the exact failure (HTTP status, response body,
 * the BACKEND_URL baked into the build) so the next occurrence produces
 * actionable data.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const hasProcessed = useRef(false);
  const [diagnostic, setDiagnostic] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const sessionId = hash.split('session_id=')[1]?.split('&')[0];

    if (!sessionId) {
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        await loginWithGoogle(sessionId);
        // Clear fragment before navigating so a refresh can't re-consume it.
        window.history.replaceState(null, '', window.location.pathname);
        toast.success('Logged in with Google!');
        navigate('/', { replace: true });
      } catch (err) {
        logger.error('[AuthCallback] Google sign-in failed:', err);
        const diag = {
          timestamp: new Date().toISOString(),
          origin: window.location.origin,
          backend_url_baked: process.env.REACT_APP_BACKEND_URL || '(unset)',
          auth_url_baked: process.env.REACT_APP_AUTH_URL || '(unset)',
          session_id_prefix: sessionId.slice(0, 8) + '…',
          error_message: err?.message || String(err),
          http_status: err?.response?.status ?? null,
          response_detail: err?.response?.data?.detail
            ?? (typeof err?.response?.data === 'string' ? err.response.data : null),
          response_body_sample: err?.response?.data
            ? JSON.stringify(err.response.data).slice(0, 300)
            : null,
          network_error: !err?.response,
          user_agent: navigator.userAgent,
        };
        // Keep URL fragment so diagnostic can reference it if user reports.
        setDiagnostic(diag);
      }
    })();
  }, [loginWithGoogle, navigate]);

  if (diagnostic) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-rose-900 flex items-center justify-center p-4"
        data-testid="auth-callback-error"
      >
        <div className="max-w-2xl w-full bg-white border border-slate-200 backdrop-blur shadow-2xl rounded-xl p-8 space-y-5">
          <div>
            <h1 className="text-2xl font-semibold text-rose-700">Google sign-in failed</h1>
            <p className="mt-1 text-sm text-slate-600">
              Your Google account verified successfully, but we couldn't complete the
              handshake with our servers. Share the diagnostic block below with support —
              it captures the exact reason without exposing your credentials.
            </p>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-xs font-mono max-h-80 overflow-auto">
            <pre data-testid="auth-diagnostic-block">{JSON.stringify(diagnostic, null, 2)}</pre>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={async () => {
                await navigator.clipboard.writeText(JSON.stringify(diagnostic, null, 2));
                toast.success('Diagnostic copied — paste this when reporting the issue.');
              }}
              className="rounded-lg bg-slate-800 text-white text-sm px-4 py-2 hover:bg-slate-700"
              data-testid="auth-diagnostic-copy-btn"
            >
              Copy diagnostic
            </button>
            <button
              onClick={() => {
                window.history.replaceState(null, '', window.location.pathname);
                navigate('/login', { replace: true });
              }}
              className="rounded-lg bg-teal-600 text-white text-sm px-4 py-2 hover:bg-teal-700"
              data-testid="auth-diagnostic-retry-btn"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4"
      data-testid="auth-callback-loading"
    >
      <div className="flex flex-col items-center gap-4 bg-white border border-slate-200 backdrop-blur shadow-2xl rounded-xl p-10">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
        <p className="text-slate-600 font-medium">Completing Google sign-in...</p>
        <p className="text-sm text-slate-500">Please wait while we verify your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;
